import React, { useState, useEffect } from 'react';
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import FormControlLabel from '@mui/material/FormControlLabel';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import TrackOrder from './trackOrder'
import './index.css'
import { connect } from 'react-redux'
import { getUpdateOrder, getOrderList, getOrderHistoryList } from 'actions/buyer';
import moment from 'moment';
import axios from 'axios';
import AppConfig from 'constants/config';
import RadioGroup from '@mui/material/RadioGroup';
import FormControl from '@mui/material/FormControl';
import Radio from '@mui/material/Radio';
import Tooltip from '@mui/material/Tooltip';
import { isHeldForBuyer, lastLogStatus, isUnholdStatus } from 'util/holdForBuyer';

/*
Campaign rewards come READY from getBuyerOrderDetails - the same payload getOrderDetails builds, one
block per tier and only for a tier that awarded something:

    detail.seller_level / .platform_level : { type: 'cashback', cashback_allocated }
                                            { type: 'gift', description, price, brand }
    line.productlevel                     : { type: 'coupon', discount_given }
                                            { type: 'gift', description, price, brand }

`discount_given` is the amount actually granted on that line, reconciled by the API against the
order's `coupon_discount`. The reconciliation this file used to carry - per-lot amount, snapshot
fallbacks, scaling - now lives in the API (controllers/order.js lineCouponAmounts), and the raw
snapshot fields it read are no longer sent. The `products` shown here come from that fetch and
nowhere else, so a line always carries its block by the time it is rendered.
*/
const cashbackOf = (block) => (block && block.type === 'cashback' ? Number(block.cashback_allocated) || 0 : 0);
const couponOf = (block) => (block && block.type === 'coupon' ? Number(block.discount_given) || 0 : 0);
const giftOf = (block) => (block && block.type === 'gift' ? block : null);
// A gift in one line - "Sipper bottle (Milton), worth ₹300". Brand and worth are dropped when the
// campaign did not name them, so a bare description never trails an empty bracket or a ₹0.
const giftText = (block) => {
  const gift = giftOf(block);
  if (!gift) return '';
  return `${gift.description}${gift.brand ? ` (${gift.brand})` : ''}${Number(gift.price) > 0 ? `, worth ₹${Number(gift.price).toFixed(2)}` : ''}`;
};
// Word for word what the cart says before the order is placed (myCart's CASHBACK_WALLET_NOTE), so a
// buyer reads the same promise on the order they are looking back at - until the order is actually
// delivered, at which point the credit has happened and the promise reads as if it still had not.
const CASHBACK_WALLET_NOTE =
  'The cashback amount will be added to your MediWallet after the order is delivered.';
const CASHBACK_WALLET_NOTE_CREDITED =
  'The cashback amount has been credited to your MediWallet.';
// Delivery is what releases the cashback, so only "Delivered" switches the note to the past tense.
// A processed, returned or lost order has not paid out, and says so with the pending wording.
const cashbackWalletNote = (order) =>
  order && order.requested === 'Delivered'
    ? CASHBACK_WALLET_NOTE_CREDITED
    : CASHBACK_WALLET_NOTE;
// Whether this order won anything at all - a cashback or a gift, either tier. Decides whether the
// rewards box below the Total is drawn.
const hasOrderReward = (detail) =>
  cashbackOf(detail?.seller_level) > 0 ||
  cashbackOf(detail?.platform_level) > 0 ||
  Boolean(giftOf(detail?.seller_level)) ||
  Boolean(giftOf(detail?.platform_level));

const styles = {
  root: {
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  icon: {
    borderRadius: '50%',
    width: 16,
    height: 16,
    boxShadow: 'inset 0 0 0 1px rgba(16,22,26,.2), inset 0 -1px 0 rgba(16,22,26,.1)',
    backgroundColor: '#f5f8fa',
    backgroundImage: 'linear-gradient(180deg,hsla(0,0%,100%,.8),hsla(0,0%,100%,0))',
    '$root.Mui-focusVisible &': {
      outline: '2px auto rgba(19,124,189,.6)',
      outlineOffset: 2,
    },
    'input:hover ~ &': {
      backgroundColor: '#ebf1f5',
    },
    'input:disabled ~ &': {
      boxShadow: 'none',
      background: 'rgba(206,217,224,.5)',
    },
  },
  checkedIcon: {
    backgroundColor: '#137cbd',
    backgroundImage: 'linear-gradient(180deg,hsla(0,0%,100%,.1),hsla(0,0%,100%,0))',
    '&:before': {
      display: 'block',
      width: 16,
      height: 16,
      backgroundImage: 'radial-gradient(#fff,#fff 28%,transparent 32%)',
      content: '""',
    },
    'input:hover ~ &': {
      backgroundColor: '#106ba3',
    },
  },
}

function StyledRadio(props) {
  return (
    <Radio
      sx={styles.root}
      disableRipple
      color="default"
      checkedIcon={<span style={{ ...styles.icon, ...styles.checkedIcon }} />}
      icon={<span style={styles.icon} />}
      {...props}
    />
  );
}

const CollapseComponent = (props) => {
  const [openModal, setOpenModal] = useState(false)
  const [details, setDetails] = useState('')
  const [acceptModal, setAcceptModal] = useState(false)
  const [cancelModal, setCancelModal] = useState(false)
  const [selectedValue, setSelectedValue] = useState('Reason not listed')
  const [products, setProducts] = useState([])
  const [partialData, setPartialData] = useState({});
  const [api, setApi] = useState(1)

  const handleClose = () => {
    setOpenModal(false)
    setDetails('')
  }

  const handleOpenModal = (details) => {
    const { mainOrderData, value } = props;
    let index = mainOrderData.findIndex((val) => val.order_id === value.orderId);
    if (index > -1) {
      setOpenModal(true)
      setDetails(mainOrderData[index])
    }
  }

  const handleAcceptModal = (details) => {
    const { mainOrderData, value } = props;
    let index = mainOrderData.findIndex((val) => val.order_id === value.orderId);
    if (index > -1) {
      setAcceptModal(true)
      setDetails(mainOrderData[index])
    }
  }

  const handleAccept = (details) => {
    const { mainOrderData, value, filterObj } = props;
    let index = mainOrderData.findIndex((val) => val.order_id === value.orderId);
    if (index > -1) {
      const data = {
        orderId: mainOrderData[index].order_id,
        status: "accept",
        acceptFromBuyer: 'accept',
        page: 1, perPage: 50,
        filter: '',
        month: props.show === true ? filterObj && filterObj.month ? filterObj.month : moment().format("MMMM") : '',
        year: props.show === true ? filterObj && filterObj.year ? Number(filterObj.year) : moment().format("GGGG") : ''
      }
      props.getUpdateOrder({ data })
    }
    handleAcceptClose()
  }

  const handleAcceptClose = () => {
    setAcceptModal(false)
  }

  const handleCancelModal = (details) => {
    const { mainOrderData, value } = props;
    let index = mainOrderData.findIndex((val) => val.order_id === value.orderId);
    if (index > -1) {
      setCancelModal(true)
    }
  }

  const handleCancel = (details) => {
    const { mainOrderData, value, filterObj, getUpdateOrder } = props;
    let index = mainOrderData.findIndex((val) => val.order_id === value.orderId);
    if (index > -1) {
      const data = {
        orderId: mainOrderData[index].order_id,
        status: "Cancelled",
        page: 1, perPage: 50, filter: '',
        month: props.show === true ? filterObj && filterObj.month ? filterObj.month : moment().format("MMMM") : '',
        year: props.show === true ? filterObj && filterObj.year ? Number(filterObj.year) : moment().format("GGGG") : '',
        reason: selectedValue,
      }
      getUpdateOrder({ data })
    }
    handleCancelClose()
  }

  const handleCancelClose = () => {
    setCancelModal(false)
  }

  const handleChange = (event) => {
    event.preventDefault();
    setSelectedValue(event.target.value)
  }

  useEffect(() => {
    if (props.collapse && api === 1) {
      axios({
        method: 'post',
        url: `${AppConfig.baseUrl}order/getBuyerOrderDetails`,
        data: { orderId: props.value.orderId },
        headers: {
          'Content-Type': 'application/json',
          'token': localStorage.getItem('buyer_token')
        }
      }).then((result) => {
        const { detail } = result.data
        setProducts(detail.products)
        console.log(detail.prepayAmt, "preppppppay")
          setPartialData({detail});
        setApi(0)
      }).catch(error => error)
    }
  }, [props.collapse, api])

  const { collapse, value, mainOrderData } = props;
  let index = mainOrderData.findIndex((val) => val.order_id === value.orderId);
  let finalPerUnitCost = 0;
  let finalPerUnitCostAndGST = 0

  return (
    <React.Fragment>
      {
        collapse === true &&
        <React.Fragment >
          <TableRow className='w-100 h-50'>
            <TableCell colSpan={4} align="right" className='w-100 h-50' >
              <div className='row ml-3'>
                <div className='col-12 d-flex justify-content-center'>
                  <List className='col-sm-12 col-xs-12 col-md-10 col-lg-10 col-xl-10'>
                    <h5 className="" style={{ textAlign: "left", marginBottom: "-20px", marginLeft: "11px" }}>{moment(value.date).format("MMM Do YYYY, hh:mm A")}</h5>
                    {
                      products.map((data, index) => {
                        if (data.discount_per > 1 && data.discount_on_product && data.discount_on_product === data.inventory_id) {
                          finalPerUnitCost = data.ePTR ? data.ePTR : data.PTR
                        } else if (data.discount_per > 1) {
                          finalPerUnitCost = data.ePTR ? data.ePTR : data.PTR
                        } else {
                          finalPerUnitCost = data.ePTR ? data.ePTR : data.PTR
                        }
                        finalPerUnitCostAndGST = Number(finalPerUnitCost) + Number(finalPerUnitCost) / 100 * Number(data.GST)
                        // One product-level campaign per line, so the block is a coupon or a gift
                        // and never both.
                        const lineCoupon = couponOf(data.productlevel);
                        const lineGift = giftOf(data.productlevel);
                        return (
                          <ListItem key={index} alignItems={'flex-start'}>
                            <ListItemText
                              className={'listMaxWidth'}
                              // `listMaxWidth` pins this column at 300px and the line total sits
                              // immediately after it, so the two ran together on longer product
                              // names. The gutter is on the content column, not the total, so the
                              // totals stay in one straight edge down the list.
                              style={{ marginRight: 32 }}
                              primary={
                                <span>
                                  {data.productName}
                                  {lineCoupon > 0 ?
                                    <Tooltip title={`₹${lineCoupon.toFixed(2)} off on this product`} placement="top">
                                      <span className="badge text-uppercase text-white bg-success ml-1 mr-0 mb-0">Coupon</span>
                                    </Tooltip> : ''}
                                  {lineGift ?
                                    <Tooltip title={`Free gift: ${giftText(data.productlevel)}`} placement="top">
                                      <span className="badge text-uppercase text-white bg-magenta ml-1 mr-0 mb-0">Gift</span>
                                    </Tooltip> : ''}
                                </span>
                              }
                              secondary={
                                ((data.discount_name && data.discount_name === 'DifferentAndDiscount') ||
                                  (data.discount_name && data.discount_name === 'SameAndDiscount')) ?
                                  <React.Fragment>
                                    PTR: ₹{(data.PTR).toFixed(2)}
                                    <br />
                                    GST({data.GST}%): ₹{(finalPerUnitCost / 100 * Number(data.GST)).toFixed(2)}
                                    <br />
                                    {data.discount_name === 'SameAndDiscount' ?
                                      <React.Fragment>
                                        Buy {data.discount_on_product.purchase} Get {data.discount_on_product.bonus} Free and {data.discount_per}% Off
                                        <br />
                                        Effective Price: ₹
                                        {data.ePTR ? data.ePTR : data.PTR}
                                      </React.Fragment>
                                      :
                                      data.discount_name === 'DifferentAndDiscount' ?
                                        <React.Fragment>
                                          Buy {data.discount_on_product.purchase} Get {data.discount_on_product.bonus} {data.discount_on_product.name} Free and {data.discount_per}% Off
                                          <br />
                                          Effective Price: ₹{data.ePTR ? data.ePTR : data.PTR}
                                        </React.Fragment> : null
                                    }
                                    <br />
                                    Final Unit Price: ₹{(finalPerUnitCostAndGST).toFixed(2)}
                                    <br />
                                    {data.expiry_date ? <React.Fragment>Expiry: {moment(data.expiry_date).format('MM/YYYY')}
                                      <br /></React.Fragment> : null}
                                    Qty: {data.quantity}
                                    <br />
                                  </React.Fragment>
                                  :
                                  data.discount_name && data.discount_name === 'Discount' ?
                                    <React.Fragment>
                                      PTR: ₹{(data.PTR).toFixed(2)}
                                      <br />
                                      Offer: {data.discount_per}% discount on PTR
                                      <br />
                                      Offer Price: ₹{(data.PTR - (data.PTR / 100 * data.discount_per)).toFixed(2)}
                                      <br />
                                      GST({data.GST}%): ₹{(finalPerUnitCost / 100 * Number(data.GST)).toFixed(2)}
                                      <br />
                                      Final Unit Price: ₹{(finalPerUnitCostAndGST).toFixed(2)}
                                      <br />
                                      {data.expiry_date ? <React.Fragment>Expiry: {moment(data.expiry_date).format('MM/YYYY')}
                                        <br /></React.Fragment> : null}
                                      Qty: {data.quantity}
                                      <br />
                                    </React.Fragment>
                                    :
                                    data.discount_on_product ?
                                      <React.Fragment>
                                        PTR: ₹{(data.PTR).toFixed(2)}
                                        <br />
                                        GST({data.GST}%): ₹{(finalPerUnitCost / 100 * Number(data.GST)).toFixed(2)}
                                        <br />
                                        {data.discount_name === 'Same' ?
                                          <React.Fragment>
                                            Buy {data.discount_on_product.purchase} Get {data.discount_on_product.bonus} Free
                                            <br />
                                            Effective Price: ₹
                                            {(data.ePTR ? data.ePTR : data.PTR).toFixed(2)}
                                          </React.Fragment>
                                          :
                                          <React.Fragment>
                                            Buy {data.discount_on_product.purchase} Get {data.discount_on_product.bonus} {data.discount_on_product.name + " Free"}
                                          </React.Fragment>
                                        }
                                        <br />
                                        Final Unit Price: ₹:{(finalPerUnitCostAndGST).toFixed(2)}
                                        <br />
                                        {data.expiry_date ? <React.Fragment>Expiry: {moment(data.expiry_date).format('MM/YYYY')}
                                          <br /></React.Fragment> : null}
                                        Qty: {data.quantity}
                                        <br />
                                      </React.Fragment>
                                      :
                                      <React.Fragment>
                                        PTR: ₹{(data.PTR).toFixed(2)}
                                        <br />
                                        GST({data.GST}%): ₹{(finalPerUnitCost / 100 * Number(data.GST)).toFixed(2)}
                                        <br />
                                        Final Unit Price: ₹:{(finalPerUnitCostAndGST).toFixed(2)}
                                        <br />
                                        {data.expiry_date ? <React.Fragment>Expiry: {moment(data.expiry_date).format('MM/YYYY')}
                                          <br /></React.Fragment> : null}
                                        Qty: {data.quantity}
                                        <br />
                                      </React.Fragment>
                              }
                            />
                            <ListItemText
                              className={'finalPrice pull-right'}
                              primary=
                              {
                                ((data.discount_name && data.discount_name === 'SameAndDiscount') || (data.discount_name && data.discount_name === 'Same')) ?
                                  <React.Fragment>
                                    ₹{(finalPerUnitCostAndGST * (((data.quantity / data.discount_on_product.purchase) * data.discount_on_product.bonus) + Number(data.quantity))).toFixed(2)}
                                  </React.Fragment>
                                  :
                                  <React.Fragment>
                                    ₹{(data.quantity * finalPerUnitCostAndGST).toFixed(2)}
                                  </React.Fragment>
                              }
                            />
                          </ListItem>
                        )
                      }
                      )
                    }
                  </List>
                </div>
              </div>
              <div className='row ml-3'>
                <div className='col-12 d-flex justify-content-center'>
                  <List className='col-sm-12 col-xs-12 col-md-10 col-lg-10 col-xl-10'>
                    {Number(value.delivery_charges) > 0 ?
                      <React.Fragment>
                        <ListItem alignItems={'flex-start'} >
                          <ListItemText
                            className={'listMaxWidth'}
                            style={{ marginRight: 32 }}
                            secondary={'Delivery Charges:'}
                          />
                          <ListItemText
                            secondary={`₹${Number(value.delivery_charges)}`}
                          />
                        </ListItem>
                      </React.Fragment>
                      :
                      null
                    }
                    {/* Shown beside the delivery charge because it behaves like one: charged on top
                        of the order value, so it is not inside the Total's total_amount either. */}
                    {Number(value.liquid_surcharge_amt) > 0 ?
                      <React.Fragment>
                        <ListItem alignItems={'flex-start'} >
                          <ListItemText
                            className={'listMaxWidth'}
                            style={{ marginRight: 32 }}
                            secondary={'Liquid delivery charge:'}
                          />
                          <ListItemText
                            secondary={`₹${Number(value.liquid_surcharge_amt).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                          />
                        </ListItem>
                      </React.Fragment>
                      :
                      null
                    }
                    <Divider />
                    {/* Prepay, Postpay and the Total are laid out as label + amount in the SAME two
                        columns as the product lines and the charges above them, rather than as a
                        space-between pair squeezed inside the 300px label column. The total then
                        falls directly under the line amounts it adds up, which is the only way to
                        read down the column and see where the figure comes from. */}
                    {(partialData?.detail?.paymentType === "Partial" &&
                      partialData?.detail?.partialPyament) ?
                      <React.Fragment>
                        <ListItem alignItems={'flex-start'} >
                          <ListItemText
                            className={'listMaxWidth'}
                            style={{ marginRight: 32 }}
                            primary={<h5>Prepay</h5>}
                          />
                          <ListItemText
                            primary={<h5>₹{Number(partialData.detail.prepayAmt || 0).toLocaleString('en-IN', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2
                            })}</h5>}
                          />
                        </ListItem>
                        <ListItem alignItems={'flex-start'} >
                          <ListItemText
                            className={'listMaxWidth'}
                            style={{ marginRight: 32 }}
                            primary={<h5>Postpay</h5>}
                          />
                          <ListItemText
                            primary={<h5>₹{Number(partialData.detail.postpayAmt || 0).toLocaleString('en-IN', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2
                            })}</h5>}
                          />
                        </ListItem>
                      </React.Fragment>
                      :
                      null
                    }
                    <ListItem alignItems={'flex-start'} >
                      <ListItemText
                        className={'listMaxWidth'}
                        style={{ marginRight: 32 }}
                        primary={<strong>Total</strong>}
                      />
                      <ListItemText
                        primary={
                          <>
                            <strong>
                              {/* The liquid surcharge is added on BOTH branches: unlike delivery it
                                  does not depend on whether delivery was charged, so an order with
                                  free delivery still owes it. */}
                              ₹{Number(value.delivery_charges) > 0
                                ? (Number(value.total_amount) + Number(value.delivery_charges) + Number(value.liquid_surcharge_amt || 0)).toLocaleString('en-IN', {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2
                                  })
                                : (Number(value.total_amount) + Number(value.liquid_surcharge_amt || 0)).toLocaleString('en-IN', {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2
                                  })}
                            </strong>
                            {/* Coupon shown from `coupon_discount`, NOT base_total_amount - total_amount.
                                That gap is not the coupon: an admin write-down widens it, so the
                                subtraction showed coupon plus write-down as one figure (a ₹45 coupon on
                                an order cut by ₹139.80 rendered as -₹192.90). The struck price is derived
                                from the payable, keeping delivery in BOTH figures as the total above
                                does, so the pair always nets to the amount actually shown.

                                It sits with the total in the amount column, directly under the figure
                                it explains. */}
                            {Number(value.coupon_discount || 0) > 0 ?
                              <div className="text-success" >
                                <span style={{ fontSize: 12 }}>
                                  <span style={{ textDecoration: 'line-through', color: '#888' }}>
                                    ₹{(Number(value.total_amount || 0) + Number(value.coupon_discount || 0) + Number(value.delivery_charges || 0) + Number(value.liquid_surcharge_amt || 0)).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </span>
                                  &nbsp;−₹{Number(value.coupon_discount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </span>
                              </div>
                              : null}
                          </>
                        }
                      />
                    </ListItem>
                    {/* What this ORDER earned, as opposed to what a single product did - both tiers
                        are won by the order itself (clearing the campaign's minimum), which is why
                        they sit under the totals and not on a line. Seller first and Medimny below
                        it, and a tier shows a cashback or a gift but never both - one campaign
                        awards one kind of reward.

                        Grouped by KIND rather than strictly by tier, so the wallet note can close
                        the cashbacks it belongs to. Listed tier-by-tier it ended up under whatever
                        came last, and a gift sitting above the wallet note reads as a gift that is
                        paid out in rupees after delivery. A gift is an item shipped with the order;
                        only the cashbacks are wallet credits, and neither of those reduces the Total.

                        Outside the totals ListItem on purpose: that one is pinned to 300px by
                        `listMaxWidth`, which broke the wallet sentence over three lines. Here the
                        box takes the full width of the list and each line reads straight across. */}
                    {hasOrderReward(partialData?.detail) ?
                      <div
                        className="mt-2 mb-2 mx-3 p-2"
                        style={{
                          borderRadius: 4,
                          background: '#f2f6ff',
                          border: '1px solid #ccd6ef',
                          // The whole collapse sits in a TableCell with align="right", so text has
                          // to be turned back the right way round here, as the date heading does.
                          textAlign: 'left',
                        }}
                      >
                        {cashbackOf(partialData?.detail?.seller_level) > 0 ?
                          <div className="text-success" style={{ fontWeight: 600, fontSize: 15 }}>
                            Seller Cashback: ₹{cashbackOf(partialData.detail.seller_level).toFixed(2)}
                          </div> : null}
                        {cashbackOf(partialData?.detail?.platform_level) > 0 ?
                          <div className="text-success" style={{ fontWeight: 600, fontSize: 15 }}>
                            Medimny Cashback: ₹{cashbackOf(partialData.detail.platform_level).toFixed(2)}
                          </div> : null}
                        {(cashbackOf(partialData?.detail?.seller_level) > 0 || cashbackOf(partialData?.detail?.platform_level) > 0) ?
                          <div style={{ fontWeight: 600, color: '#000', fontSize: 15 }}>
                            {cashbackWalletNote(mainOrderData[index])}
                          </div> : null}
                        {giftOf(partialData?.detail?.seller_level) ?
                          <div style={{ color: '#ef6c00', fontWeight: 600, fontSize: 15 }}>
                            Seller Gift: {giftText(partialData.detail.seller_level)}
                          </div> : null}
                        {giftOf(partialData?.detail?.platform_level) ?
                          <div style={{ color: '#ef6c00', fontWeight: 600, fontSize: 15 }}>
                            Medimny Gift: {giftText(partialData.detail.platform_level)}
                          </div> : null}
                      </div> : null}
                  </List>
                </div>
              </div>
              <div className="col-6 d-flex justify-content-center"></div>
              <Divider />
              <div className="col-12 d-flex justify-content-center">
                {partialData?.detail?.requestedProd?.some(
                  (prod) => prod.avalQuantity === 0,
                ) && (
                  <div className=" p-3  w-100 text-start">
                    <h6 className="fw-bold mb-2 text-dark">
                      Change Request by{" "}
                      {partialData?.detail?.seller_id?.company_name}
                    </h6>

                    <p className="mb-2 text-muted">
                      The following items have been removed:
                    </p>

                    <div>
                      {partialData?.detail?.requestedProd
                        ?.filter((prod) => prod.avalQuantity === 0)
                        .map((removedProd, index) => (
                          <div
                            key={index}
                            className="d-flex flex-column gap-2 my-2  text-danger border border-danger rounded px-3 py-2 bg-white"
                          >
                            {index + 1}. {removedProd.name}{" "}
                            {`( Ordered quantity: ${removedProd.reqQuantity}, Available quantity: ${removedProd.avalQuantity})`}
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </div>
              <Divider />
              <div className='row mt-3 mr-2 flex-row-reverse'>
                <div className=''>
                  <div className='list-action mx-auto'>
                    {/* Held for the buyer, and so checked before everything else - the hold outranks
                        even a Requested order, which it is allowed to sit on. The order presents as
                        Processed, so it gets that branch's View Invoice control, DISABLED: no
                        invoice exists yet. No Cancel either - the API refuses a buyer cancellation
                        while the hold is on (see util/holdForBuyer.js). */}
                    {isHeldForBuyer(mainOrderData[index]) ?
                      <div>
                        <Button className="jr-btn text-uppercase text-info" disabled>
                          View Invoice
                        </Button>
                        <Button className="jr-btn text-uppercase text-success" onClick={() => handleOpenModal(value)}>
                          Track
                        </Button>
                      </div>
                      :
                      mainOrderData[index].requested === "Requested" ?
                      <div>
                        <Button className="jr-btn text-uppercase text-primary" onClick={() => handleAcceptModal(value)}>
                          Accept
                        </Button>
                        <Button className="jr-btn text-uppercase text-danger" onClick={() => handleCancelModal(value)}>
                          Cancel
                        </Button>
                        <Button className="jr-btn text-uppercase text-success" onClick={() => handleOpenModal(value)}>
                          Track
                        </Button>
                      </div>
                      :
                      // A RELEASED order is a New one again, so it gets New's actions back - Cancel
                      // included, which the API allows again the moment the flag comes off.
                      lastLogStatus(mainOrderData[index]) === "New" ||
                        lastLogStatus(mainOrderData[index]) === "OnHold" ||
                        isUnholdStatus(lastLogStatus(mainOrderData[index]))
                        ?
                        <div>
                          <Button className="jr-btn text-uppercase text-danger" onClick={() => handleCancelModal(value)}>
                            Cancel
                          </Button>
                          <Button className="jr-btn text-uppercase text-success" onClick={() => handleOpenModal(value)}>
                            Track
                          </Button>
                        </div>
                        :
                        (mainOrderData[index].requested === "Delivered" || mainOrderData[index].requested === "Processed"
                          || mainOrderData[index].requested === "RTO" || mainOrderData[index].requested === "LOST"
                          || mainOrderData[index].requested === "RTO after Delivery") ?
                          <div>
                            <a href={`${AppConfig.baseUrl}invoice/${mainOrderData[index].uploadedInvoice}`} target='_blank' rel="noreferrer">
                              <Button className="jr-btn text-uppercase text-info">
                                View Invoice
                              </Button>
                            </a>
                            <Button className="jr-btn text-uppercase text-success" onClick={() => handleOpenModal(value)}>
                              Track
                            </Button>
                          </div>
                          :
                          <Button className="jr-btn text-uppercase text-success" onClick={() => handleOpenModal(value)}>
                            Track
                          </Button>
                    }
                  </div>
                </div>
              </div>
            </TableCell>
          </TableRow>
        </React.Fragment>
      }

      {/* -------------------------- TrackOrder --------------------------------------- */}

      <Dialog open={openModal} onClose={handleClose} fullWidth={true}>
        <DialogTitle id="alert-dialog-title">{"Order tracking details"}</DialogTitle>
        <DialogContent>
          <TrackOrder orderDetails={details} />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="secondary" autoFocus> Close </Button>
        </DialogActions>
      </Dialog>

      {/* -------------------------- Accept --------------------------------------- */}

      <Dialog open={acceptModal} onClose={handleAcceptClose} fullWidth={true}>
        <DialogTitle id="alert-dialog-title">Accept Changes</DialogTitle>
        <DialogContent>
          Are you sure you want to accept changes?
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAcceptClose} color="secondary" autoFocus> Close </Button>
          <Button className="jr-btn text-uppercase text-primary" onClick={() => handleAccept(value)}> Accept </Button>
        </DialogActions>
      </Dialog>

      {/* -------------------------- Cancel --------------------------------------- */}

      <Dialog open={cancelModal} onClose={handleCancelClose} fullWidth={true}>
        <DialogTitle>
          Cancel Order
          <DialogContentText className="mt-1 mb-0">
            Are you sure you want to cancel the order ?
          </DialogContentText>
        </DialogTitle>
        <DialogContent>
          <FormControl component="fieldset">
            <RadioGroup aria-label="reason" name="customized-radios" value={selectedValue} onChange={handleChange}>
              <FormControlLabel value="Late delivery" control={<StyledRadio />} label="Late delivery" />
              <FormControlLabel value="Better price somewhere" control={<StyledRadio />} label="Better price somewhere" />
              <FormControlLabel value="Order by mistake" control={<StyledRadio />} label="Order by mistake" />
              <FormControlLabel value="Reason not listed" control={<StyledRadio />} label="Reason not listed" />
            </RadioGroup>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelClose} color="secondary" autoFocus> Close </Button>
          <Button className="jr-btn text-uppercase text-primary" onClick={() => handleCancel(value)}> Cancel Order </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  )
}

const mapStateToProps = ({ buyer }) => {
  const { updateOrder } = buyer;
  return { updateOrder }
};

export default connect(mapStateToProps, { getUpdateOrder, getOrderList, getOrderHistoryList })(CollapseComponent);

